export type HapticEvent = "brick_break" | "level_complete" | "game_over" | "button_tap";
export type BombHapticType = "bomb_3" | "bomb_4" | "bomb_5";

type QueuedHapticKind = BombHapticType | "wipe";
type WebHapticsInstance = { trigger: (input?: unknown, options?: { intensity?: number }) => Promise<void> };
type HapticVector = Array<{ duration: number; delay?: number; intensity?: number }>;

const HAPTIC_THROTTLE_MS = 60;
const BOMB_5_SECOND_PULSE_DELAY_MS = 70;
const BOMB_CHAIN_AFTERSHOCK_SPACING_MS = 65;
const BOMB_CHAIN_AFTERSHOCK_MAX = 6;
const GAME_OVER_VIBRATION_DURATION_MS = 2000;

function hapticRank(kind: QueuedHapticKind): number {
  if (kind === "bomb_5") {
    return 4;
  }
  if (kind === "bomb_4") {
    return 3;
  }
  if (kind === "bomb_3") {
    return 2;
  }
  return 1; // WIPE is intentionally below bomb_3
}

class HapticManager {
  private enabled = true;
  private initialized = false;
  private webHaptics: WebHapticsInstance | null = null;
  private gestureBoundPlatform = false;
  private cooldownUntilMs = 0;
  private queuedKind: QueuedHapticKind | null = null;
  private queuedTimer: ReturnType<typeof setTimeout> | null = null;
  private deferredPulseTimers = new Set<ReturnType<typeof setTimeout>>();

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.gestureBoundPlatform = this.isGestureBoundIOSWeb();

    try {
      const { WebHaptics } = await import("web-haptics");
      this.webHaptics = new WebHaptics() as unknown as WebHapticsInstance;
    } catch {
      this.webHaptics = null;
    }
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      this.clearPending();
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isGestureBoundPlatform(): boolean {
    return this.gestureBoundPlatform;
  }

  triggerBomb(type: BombHapticType): void {
    if (this.gestureBoundPlatform) {
      return;
    }
    this.enqueue(type);
  }

  triggerWipe(): void {
    if (this.gestureBoundPlatform) {
      return;
    }
    this.enqueue("wipe");
  }

  triggerBombFromGesture(type: BombHapticType): void {
    if (!this.gestureBoundPlatform) {
      return;
    }
    this.enqueue(type);
  }

  triggerWipeFromGesture(): void {
    if (!this.gestureBoundPlatform) {
      return;
    }
    this.enqueue("wipe");
  }

  triggerBombChainAftershocks(type: BombHapticType, explosionStepCount: number): void {
    if (!this.enabled) {
      return;
    }
    const safeStepCount = Math.max(0, Math.floor(explosionStepCount));
    const pulseCount = Math.min(BOMB_CHAIN_AFTERSHOCK_MAX, Math.max(0, safeStepCount - 1));
    if (pulseCount <= 0) {
      return;
    }
    const profile = this.aftershockProfile(type);
    for (let i = 0; i < pulseCount; i += 1) {
      this.scheduleDeferredPulse(() => {
        this.dispatchPulse(
          [{ duration: profile.durationMs }],
          [profile.durationMs],
          { intensity: profile.intensity }
        );
      }, BOMB_CHAIN_AFTERSHOCK_SPACING_MS * (i + 1));
    }
  }

  triggerRowRipSweep(): void {
    if (this.gestureBoundPlatform) {
      return;
    }
    this.dispatchRowRipSweep();
  }

  triggerColCutSweep(): void {
    if (this.gestureBoundPlatform) {
      return;
    }
    this.dispatchColCutSweep();
  }

  triggerZigzagBurst(): void {
    if (this.gestureBoundPlatform) {
      return;
    }
    this.dispatchZigzagBurst();
  }

  triggerRowRipSweepFromGesture(): void {
    if (!this.gestureBoundPlatform) {
      return;
    }
    this.dispatchRowRipSweep();
  }

  triggerColCutSweepFromGesture(): void {
    if (!this.gestureBoundPlatform) {
      return;
    }
    this.dispatchColCutSweep();
  }

  triggerZigzagBurstFromGesture(): void {
    if (!this.gestureBoundPlatform) {
      return;
    }
    this.dispatchZigzagBurst();
  }

  // Kept for compatibility with old call sites. Most legacy events are still no-ops.
  fire(event: HapticEvent): void {
    if (!this.enabled) {
      return;
    }
    if (event === "game_over") {
      this.triggerGameOver();
    }
  }

  private enqueue(kind: QueuedHapticKind): void {
    if (!this.enabled) {
      return;
    }
    if (this.gestureBoundPlatform) {
      this.enqueueImmediate(kind);
      return;
    }
    this.enqueueBuffered(kind);
  }

  private enqueueBuffered(kind: QueuedHapticKind): void {
    if (this.queuedKind == null || hapticRank(kind) > hapticRank(this.queuedKind)) {
      this.queuedKind = kind;
    }
    if (this.queuedTimer != null) {
      return;
    }
    this.queuedTimer = setTimeout(() => {
      this.queuedTimer = null;
      const queued = this.queuedKind;
      this.queuedKind = null;
      if (!this.enabled || queued == null) {
        return;
      }
      this.dispatchQueued(queued);
    }, HAPTIC_THROTTLE_MS);
  }

  private enqueueImmediate(kind: QueuedHapticKind): void {
    const now = this.nowMs();
    // Fire the leading edge immediately, then coalesce trailing events during cooldown.
    if (now >= this.cooldownUntilMs && this.queuedTimer == null && this.queuedKind == null) {
      this.dispatchQueued(kind);
      this.cooldownUntilMs = now + HAPTIC_THROTTLE_MS;
      return;
    }

    if (this.queuedKind == null || hapticRank(kind) > hapticRank(this.queuedKind)) {
      this.queuedKind = kind;
    }
    this.ensureQueuedTimer();
  }

  private ensureQueuedTimer(): void {
    if (this.queuedTimer != null) {
      return;
    }
    const delayMs = Math.max(0, this.cooldownUntilMs - this.nowMs());
    this.queuedTimer = setTimeout(() => {
      this.queuedTimer = null;
      this.flushQueued();
    }, delayMs);
  }

  private flushQueued(): void {
    if (!this.enabled) {
      this.queuedKind = null;
      return;
    }
    const queued = this.queuedKind;
    this.queuedKind = null;
    if (queued == null) {
      return;
    }
    this.dispatchQueued(queued);
    this.cooldownUntilMs = this.nowMs() + HAPTIC_THROTTLE_MS;
    if (this.queuedKind != null) {
      this.ensureQueuedTimer();
    }
  }

  private dispatchQueued(kind: QueuedHapticKind): void {
    switch (kind) {
      case "bomb_3":
        this.dispatchPulse(
          [{ duration: 360 }],
          [45, 30, 40],
          { intensity: 0.6 }
        );
        return;
      case "bomb_4":
        this.dispatchPulse(
          [{ duration: 700 }],
          [95, 55, 75],
          { intensity: 0.9 }
        );
        return;
      case "bomb_5":
        // User-requested heavy bomb pattern.
        this.dispatchPulse(
          [{ duration: 1300 }],
          [180, 100, 180],
          { intensity: 1 }
        );
        this.scheduleDeferredPulse(() => {
          this.dispatchPulse([{ duration: 120 }], [120], { intensity: 1 });
        }, BOMB_5_SECOND_PULSE_DELAY_MS);
        return;
      case "wipe":
        this.dispatchPulse(
          [
            { duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 },
            { delay: 117, duration: 45, intensity: 0.72 }
          ],
          [45, 117, 45, 117, 45, 117, 45, 117, 45, 117, 45, 117, 45, 117, 45, 117, 45, 117, 45],
          { intensity: 0.72 }
        );
        return;
    }
  }

  private dispatchRowRipSweep(): void {
    if (!this.enabled) {
      return;
    }
    this.dispatchPulse(
      [
        { duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 }
      ],
      [20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20],
      { intensity: 0.82 }
    );
  }

  private dispatchColCutSweep(): void {
    if (!this.enabled) {
      return;
    }
    this.dispatchPulse(
      [
        { duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 },
        { delay: 56, duration: 20, intensity: 0.82 }
      ],
      [20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20, 56, 20],
      { intensity: 0.82 }
    );
  }

  private dispatchZigzagBurst(): void {
    if (!this.enabled) {
      return;
    }
    this.dispatchPulse(
      [
        { duration: 35, intensity: 1 },
        { delay: 45, duration: 35, intensity: 1 },
        { delay: 45, duration: 35, intensity: 1 },
        { delay: 45, duration: 35, intensity: 1 },
        { delay: 45, duration: 35, intensity: 1 }
      ],
      [35, 45, 35, 45, 35, 45, 35, 45, 35],
      { intensity: 1 }
    );
  }

  private dispatchPulse(
    webInput: HapticVector | string,
    vibratePattern: number | number[],
    options?: { intensity?: number }
  ): void {
    if (this.webHaptics != null) {
      try {
        const run = this.webHaptics.trigger(webInput, options);
        void run.catch(() => {
          this.triggerVibrate(vibratePattern);
        });
        return;
      } catch {
        // fall through to vibration fallback
      }
    }
    this.triggerVibrate(vibratePattern);
  }

  private triggerVibrate(pattern: number | number[]): void {
    const nav = this.getNavigator();
    if (nav == null || typeof nav.vibrate !== "function") {
      return;
    }
    try {
      nav.vibrate(pattern);
    } catch {
      // no-op on unsupported/blocked platforms
    }
  }

  private aftershockProfile(type: BombHapticType): { durationMs: number; intensity: number } {
    if (type === "bomb_5") {
      return { durationMs: 130, intensity: 1 };
    }
    if (type === "bomb_4") {
      return { durationMs: 110, intensity: 0.92 };
    }
    return { durationMs: 90, intensity: 0.82 };
  }

  private triggerGameOver(): void {
    this.clearPending();
    this.dispatchPulse(
      [{ duration: GAME_OVER_VIBRATION_DURATION_MS }],
      [GAME_OVER_VIBRATION_DURATION_MS],
      { intensity: 1 }
    );
  }

  private scheduleDeferredPulse(fn: () => void, delayMs: number): void {
    const timer = setTimeout(() => {
      this.deferredPulseTimers.delete(timer);
      if (!this.enabled) {
        return;
      }
      fn();
    }, delayMs);
    this.deferredPulseTimers.add(timer);
  }

  private clearPending(): void {
    this.queuedKind = null;
    this.cooldownUntilMs = 0;
    if (this.queuedTimer != null) {
      clearTimeout(this.queuedTimer);
      this.queuedTimer = null;
    }
    for (const timer of this.deferredPulseTimers) {
      clearTimeout(timer);
    }
    this.deferredPulseTimers.clear();
  }

  private getNavigator(): Navigator | null {
    if (typeof navigator === "undefined") {
      return null;
    }
    return navigator;
  }

  private nowMs(): number {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
    return Date.now();
  }

  private isLikelyIOS(): boolean {
    const nav = this.getNavigator();
    if (nav == null) {
      return false;
    }
    const ua = nav.userAgent ?? "";
    if (/iPad|iPhone|iPod/.test(ua)) {
      return true;
    }
    const platform = (nav as Navigator & { platform?: string }).platform ?? "";
    const maxTouchPoints = (nav as Navigator & { maxTouchPoints?: number }).maxTouchPoints ?? 0;
    return platform === "MacIntel" && maxTouchPoints > 1;
  }

  private isGestureBoundIOSWeb(): boolean {
    return this.isLikelyIOS();
  }
}

export const hapticManager = new HapticManager();
